import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Button,
    SimpleGrid,
    Flex,
    Icon,
    HStack,
    IconButton,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Link,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { FaAppleAlt, FaDumbbell, FaBrain, FaMoon, FaStethoscope, FaPills, FaCompass } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SecureMembershipButton } from "./authentication";
import PublicForm from "./leadflow/PublicForm";
import { motion } from "framer-motion";
import AdminModuleLinks from "../components/AdminModuleLinks";
import { HowItWorksStepCards } from "./HowItWorksSteps";

// Module scope on purpose: created inside Homepage, every re-render (scroll, carousel timer)
// made a new component type, remounting the waitlist + its form and replaying the fade-in.
const MotionBox = motion(Box);
const waitlistReveal = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0 } };

/*
 * Copy follows Sam Manger's "SurgReady website feedback" (27 Jul 2026): AHPRA advertising rules —
 * no promised outcomes ("safer surgery", "faster recovery", "back to life sooner"), research figures
 * only as referenced research evidence, "evidence-informed", Australian spelling ("optimise").
 * Every link is a real link (as={RouterLink}) so it can be opened in a new tab.
 */

/** Research evidence for the Science section — Vancouver style, the papers Sam supplied. */
const RESEARCH_FIGURES = [
    { value: "56%", label: "lower odds of complications after surgery", ref: 1 },
    { value: "2.2 days", label: "shorter stay in hospital", ref: 2 },
];
const REFERENCES = [
    {
        text: "Clifford K, Woodfield JC, Tait W, Campbell HA, Baldi JC. Association of preoperative high-intensity interval training with cardiorespiratory fitness and postoperative outcomes among adults undergoing major surgery: a systematic review and meta-analysis. JAMA Netw Open. 2023;6(6):e2320527.",
        href: "https://pubmed.ncbi.nlm.nih.gov/37389875/",
    },
    {
        text: "Gillis C, Buhler K, Bresee L, Carli F, Gramlich L, Culos-Reed N, et al. Effects of nutritional prehabilitation, with and without exercise, on outcomes of patients who undergo colorectal surgery: a systematic review and meta-analysis. Gastroenterology. 2018;155(2):391-410.e4.",
        href: "https://pubmed.ncbi.nlm.nih.gov/29750973/",
    },
];

/** "Got questions? We've got answers." — drafted for Sam to check; only what the program does today. */
const FAQS = [
    {
        q: "Who is SurgReady for?",
        a: "Adults who have a planned operation coming up and want to get their body and mind ready for it.",
    },
    {
        q: "What's included?",
        a: "Online education modules covering nutrition, supplements, physical activity, mind-body, sleep, substances, social connection, medication management and chronic disease management, plus a health assessment and a health record you can download, print or send to your GP.",
    },
    {
        q: "When should I start?",
        a: "As early as you can before your surgery, so you have time to work through the modules. You can start at any point and go at your own pace.",
    },
    {
        q: "Does SurgReady replace my doctor or surgical team?",
        a: "No. SurgReady provides general educational information. It doesn't replace personalised advice from your own healthcare team. Never stop or change your medicines without talking to them first.",
    },
    {
        q: "Can I share my progress with my healthcare team?",
        a: "Yes. Your health record brings together everything you've answered, so you can download it, print it or email it to your GP.",
    },
    {
        q: "Who created SurgReady?",
        a: "A team of clinicians and researchers, including a GP, an anaesthetist, a pharmacist, a dietitian, an exercise physiologist and a psychologist. You can meet them on our About page.",
    },
];

const Homepage = () => {
    const [scrollY, setScrollY] = useState(0);
    const [showFloatingCTA, setShowFloatingCTA] = useState(false);
    const [floatingButtonStyle, setFloatingButtonStyle] = useState<'light' | 'dark'>('light');
    const heroRef = useRef<HTMLDivElement>(null);
    const sectionsRef = useRef<HTMLDivElement[]>([]);
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerVisible, setFooterVisible] = useState(false);
    const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
    const [featureProgress, setFeatureProgress] = useState(0);
    const FEATURE_DURATION = 4500;
    const carouselVideoRef = useRef<HTMLVideoElement>(null);
    const floatingButtonRef = useRef<HTMLButtonElement>(null);
    const carouselSectionRef = useRef<HTMLDivElement>(null);
    const coveredRowRef = useRef<HTMLDivElement>(null);
    // "We have you covered" cards: which one is turned over to show its description (the + button).
    const [openModule, setOpenModule] = useState<number | null>(null);
    const scrollCovered = (dir: 1 | -1) => coveredRowRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

    const featureItems = [
        {
            title: "Evidence-informed",
            description: "Built on peer-reviewed research and clinical best practice, and updated as the evidence grows.",
            video: "/assets/video/evidence-based.mp4"
        },
        {
            title: "Built by clinicians and researchers",
            description: "Developed by a multidisciplinary team of doctors, allied health professionals and researchers.",
            video: "/assets/video/doctor-led.mp4"
        },
        {
            title: "Your own personalised plan",
            description: "Supports you to create your own personalised plan and get the most out of your pre-surgical journey.",
            video: "/assets/video/personalized-care.mp4"
        },
        {
            title: "Comprehensive program",
            description: "From nutrition and movement to sleep, medicines and mindset, the program covers the key areas of getting ready for surgery.",
            video: "/assets/video/comprehensive-support.mp4"
        }
    ];

    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;
        setScrollY(currentScrollY);

        setShowFloatingCTA(currentScrollY > 100);

        // Use the floating button's position to detect background color
        let isDarkBackground = true;

        if (floatingButtonRef.current) {
            const buttonRect = floatingButtonRef.current.getBoundingClientRect();
            const buttonCenterY = buttonRect.top + buttonRect.height / 2;

            sectionsRef.current.forEach((section) => {
                if (section) {
                    const sectionRect = section.getBoundingClientRect();

                    // Check if button center is within this section
                    if (buttonCenterY >= sectionRect.top && buttonCenterY <= sectionRect.bottom) {
                        const bgColor = section.dataset.bgtype;
                        isDarkBackground = bgColor !== 'light';
                    }
                }
            });
        }

        setFloatingButtonStyle(isDarkBackground ? 'light' : 'dark');
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        const footerObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setFooterVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.1 }
        );

        if (footerRef.current) {
            footerObserver.observe(footerRef.current);
        }

        return () => {
            footerObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const progressInterval = setInterval(() => {
            setFeatureProgress((prev) => {
                if (prev >= 100) {
                    setActiveFeatureIndex((prevIndex) => (prevIndex + 1) % featureItems.length);
                    return 0;
                }
                return prev + (100 / (FEATURE_DURATION / 50));
            });
        }, 50);

        return () => clearInterval(progressInterval);
    }, [featureItems.length]);

    useEffect(() => {
        if (carouselVideoRef.current) {
            const video = carouselVideoRef.current;
            video.src = featureItems[activeFeatureIndex].video;
            video.load();
            video.play().catch(() => { });
        }
    }, [activeFeatureIndex]);

    // Reset carousel to first item when section becomes visible
    useEffect(() => {
        const carouselObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveFeatureIndex(0);
                        setFeatureProgress(0);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (carouselSectionRef.current) {
            carouselObserver.observe(carouselSectionRef.current);
        }

        return () => {
            carouselObserver.disconnect();
        };
    }, []);

    const optimizationModules = [
        {
            title: "Nutrition",
            icon: FaAppleAlt,
            description: "Eating well to support healing and recovery",
            image: "url('https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800&q=80')"
        },
        {
            title: "Supplements",
            icon: FaPills,
            description: "What the evidence says about supplements before surgery",
            image: "url('https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&q=80')"
        },
        {
            title: "Prehab",
            icon: FaDumbbell,
            description: "Structured exercise to build strength and fitness",
            image: "url('https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80')"
        },
        {
            title: "Mind-Body",
            icon: FaBrain,
            description: "Mindfulness and ways to manage stress and worry",
            image: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80')"
        },
        {
            title: "Sleep",
            icon: FaMoon,
            description: "Better sleep to support healing",
            image: "url('https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=800&q=80')"
        },
        {
            title: "Chronic Care",
            icon: FaStethoscope,
            description: "Looking after ongoing health conditions before surgery",
            image: "url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80')"
        },
        {
            title: "Medications",
            icon: FaPills,
            description: "Understanding your medicines and what to ask your team",
            image: "url('https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=800&q=80')"
        },
        {
            title: "Navigation",
            icon: FaCompass,
            description: "Finding your way through your care, and who to ask",
            image: "url('https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80')"
        }
    ];


    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <NavbarWithCallToAction scrollY={scrollY} />
            {/* Admins only (self-gating): quick links to every module, curatable, saved to their record — TMS pattern. */}
            <AdminModuleLinks />

            {/* Hero Section */}
            <Box
                ref={heroRef}
                minH="100vh"
                position="relative"
                display="flex"
                alignItems="center"
                overflow="hidden"
                data-bgtype="dark"
            >
                {/* Background Video */}
                <Box
                    as="video"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    zIndex={0}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/surgready/hero-image.png"
                    sx={{ pointerEvents: "none" }}
                >
                    <source src="/assets/video/hero-video.mp4" type="video/mp4" />
                </Box>

                {/* Dark Gradient Overlay - lighter for better video visibility */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={1}
                    bg="rgba(0,0,0,0.45)"
                />

                <Container maxW="1400px" position="relative" zIndex={2}>
                    <VStack align="center" spacing={8} textAlign="center" w="full">
                        <Heading
                            as="h1"
                            fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                            fontWeight="500"
                            lineHeight="1.05"
                            letterSpacing="-0.02em"
                        >
                            Prepare for surgery.
                            <br />
                            Support your recovery.
                        </Heading>

                        <Text
                            fontSize={{ base: "lg", md: "xl" }}
                            color="white"
                            maxW="600px"
                            lineHeight="1.7"
                            fontWeight="400"
                            opacity={0.9}
                        >
                            SurgReady is an evidence-informed online program that helps you optimise
                            your health before surgery and get back to what matters most.
                        </Text>

                        <HStack pt={4} spacing={4} justify="center">
                            <Button
                                as={RouterLink}
                                to="/pricing"
                                size="lg"
                                px={10}
                                py={7}
                                borderRadius="full"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing="0.1em"
                                bg="#4A90E2"
                                color="white"
                                boxShadow="0 4px 20px rgba(74, 144, 226, 0.4)"
                                transition="all 0.3s ease"
                                fontSize="lg"
                                _hover={{
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 30px rgba(74, 144, 226, 0.5)",
                                    bg: "#3DA2DD",
                                }}
                            >
                                Get SurgReady
                            </Button>
                            <Button
                                variant="outline"
                                size="lg"
                                borderColor="whiteAlpha.400"
                                color="white"
                                textTransform="uppercase"
                                letterSpacing="0.1em"
                                fontWeight="500"
                                _hover={{
                                    bg: "whiteAlpha.100",
                                    borderColor: "whiteAlpha.600",
                                }}
                                as={RouterLink}
                                to="/about"
                                px={10}
                                py={7}
                                borderRadius="full"
                                bg="transparent"
                                border="1px solid"
                            >
                                Learn More
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            {/* Second Hero - Value Proposition */}
            <Box
                minH={{ base: "auto", md: "80vh" }}
                py={{ base: 16, md: 0 }}
                display="flex"
                alignItems="center"
                position="relative"
                overflow="hidden"
                isolation="isolate"
            >
                {/* Video Background */}
                <Box
                    as="video"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    zIndex={0}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/surgready/running-couple.png"
                    sx={{
                        pointerEvents: "none",
                    }}
                >
                    <source src="/assets/video/surgical-journey.mp4" type="video/mp4" />
                </Box>

                {/* Dark Gradient Overlay - fixed at 55% darkness */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={1}
                    bg="rgba(0,0,0,0.55)"
                />

                <Container maxW="1400px" position="relative" zIndex={2} pl={{ base: 6, md: 16, lg: 24 }}>
                    <VStack align="flex-start" spacing={6} maxW="600px" textAlign="left">
                        <Heading
                            as="h2"
                            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                            fontWeight="500"
                            lineHeight="1.1"
                            letterSpacing="-0.02em"
                        >
                            Everything you need to prepare for surgery, in one simple program.
                        </Heading>
                        <Text
                            fontSize={{ base: "lg", md: "xl" }}
                            color="white"
                            lineHeight="1.7"
                            fontWeight="400"
                        >
                            Four steps, at your own pace, alongside your own healthcare team.
                        </Text>
                        <Button
                            size="lg"
                            bg="white"
                            color="black"
                            _hover={{
                                bg: "whiteAlpha.900",
                                transform: "translateY(-2px)",
                            }}
                            as="a"
                            href="#how-it-works"
                            px={10}
                            py={7}
                            borderRadius="full"
                            fontWeight="500"
                            letterSpacing="0.02em"
                            transition="all 0.3s"
                        >
                            See how it works
                            <ChevronRightIcon ml={2} />
                        </Button>
                    </VStack>
                </Container>
            </Box>

            {/* How it works — Sam: "I wonder if we can incorporate into the home page" (27 Jul 2026) */}
            <Box id="how-it-works" bg="#0a0a0a" py={{ base: 16, md: 24 }} scrollMarginTop="64px">
                <Container maxW="1400px">
                    <VStack spacing={4} mb={12} textAlign="center">
                        <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.25em" color="whiteAlpha.600">
                            How it works
                        </Text>
                        <Heading as="h2" fontSize={{ base: "3xl", md: "5xl" }} fontWeight="500" letterSpacing="-0.02em">
                            Four steps to getting SurgReady.
                        </Heading>
                    </VStack>
                    <HowItWorksStepCards />
                    <Flex justify="center" mt={10}>
                        <Button as={RouterLink} to="/how-it-works" variant="outline" size="lg" minH="44px" borderRadius="full" borderColor="whiteAlpha.400" color="white" _hover={{ bg: "whiteAlpha.100" }}>
                            More about how it works
                        </Button>
                    </Flex>
                </Container>
            </Box>

            {/* Optimisation modules - Whoop-style image panels */}
            <Box
                bg="#f5f5f5"
                py={{ base: 16, md: 24 }}
                data-bgtype="light"
                ref={(el: HTMLDivElement) => { if (el) sectionsRef.current[0] = el; }}
            >
                <Container maxW="1400px" mb={10}>
                    <Flex justify="space-between" align="flex-end" gap={4}>
                    <VStack spacing={4} align="flex-start">
                        <Heading
                            as="h2"
                            fontSize={{ base: "3xl", md: "5xl" }}
                            fontWeight="500"
                            letterSpacing="-0.02em"
                            color="black"
                            lineHeight="1.1"
                        >
                            We have you covered.
                        </Heading>
                        <Text
                            fontSize={{ base: "md", md: "lg" }}
                            color="gray.600"
                            fontWeight="400"
                            maxW="600px"
                        >
                            Our evidence-informed program covers the key areas of getting ready for surgery, one step at a time.
                        </Text>
                    </VStack>
                    {/* Sam: "the individual pictures don't move across" — arrows scroll the row on any screen. */}
                    <HStack spacing={3} flexShrink={0}>
                        <IconButton aria-label="Show the previous topics" title="Show the previous topics" icon={<ChevronLeftIcon boxSize={6} />} onClick={() => scrollCovered(-1)} borderRadius="full" w="44px" h="44px" bg="white" color="black" boxShadow="0 2px 8px rgba(0,0,0,0.15)" _hover={{ bg: "gray.100" }} />
                        <IconButton aria-label="Show the next topics" title="Show the next topics" icon={<ChevronRightIcon boxSize={6} />} onClick={() => scrollCovered(1)} borderRadius="full" w="44px" h="44px" bg="white" color="black" boxShadow="0 2px 8px rgba(0,0,0,0.15)" _hover={{ bg: "gray.100" }} />
                    </HStack>
                    </Flex>
                </Container>

                {/* Horizontal Scrolling Cards */}
                <Box
                    ref={coveredRowRef}
                    overflowX="auto"
                    pb={4}
                    sx={{
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    <HStack
                        spacing={4}
                        px={{ base: 4, md: 8 }}
                        minW="max-content"
                    >
                        {optimizationModules.map((module, index) => (
                            <Box
                                key={index}
                                position="relative"
                                minW={{ base: "280px", md: "320px" }}
                                h={{ base: "400px", md: "480px" }}
                                borderRadius="3xl"
                                overflow="hidden"
                                transition="all 0.3s ease"
                                _hover={{
                                    transform: "scale(1.02)",
                                }}
                            >
                                {/* Background Image */}
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    bgImage={module.image}
                                    bgSize="cover"
                                    bgPosition="center"
                                    zIndex={0}
                                />

                                {/* White Gradient Overlay at Top - Whoop style (subtle, not solid) */}
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    h="50%"
                                    bgGradient="linear(to-b, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0) 100%)"
                                    zIndex={1}
                                />

                                {/* Content */}
                                <Flex
                                    position="relative"
                                    zIndex={2}
                                    h="100%"
                                    flexDirection="column"
                                    justifyContent="space-between"
                                    p={5}
                                >
                                    <Box pt={4} px={1}>
                                        <Heading
                                            fontSize={{ base: "xl", md: "26px" }}
                                            fontWeight="500"
                                            color="black"
                                            lineHeight="1.25"
                                            maxW="200px"
                                            fontFamily="'Outfit', sans-serif"
                                            letterSpacing="-0.02em"
                                        >
                                            {module.title}
                                        </Heading>
                                    </Box>

                                    {/* Sam: "the + symbols don't do anything" — + now turns the card over to show what the topic covers. */}
                                    {openModule === index && (
                                        <Box bg="rgba(255,255,255,0.95)" color="black" borderRadius="xl" p={4} mx={1}>
                                            <Text fontSize="md" lineHeight="1.5">{module.description}</Text>
                                        </Box>
                                    )}
                                    <Flex justify="flex-end">
                                        <Box
                                            as="button"
                                            type="button"
                                            aria-label={openModule === index ? `Hide what ${module.title} covers` : `Show what ${module.title} covers`}
                                            title={openModule === index ? "Hide the description" : "What this topic covers"}
                                            aria-expanded={openModule === index}
                                            onClick={() => setOpenModule(openModule === index ? null : index)}
                                            w="44px"
                                            h="44px"
                                            borderRadius="full"
                                            bg="white"
                                            position="relative"
                                            transition="all 0.2s"
                                            transform={openModule === index ? "rotate(45deg)" : undefined}
                                            boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                                            _hover={{
                                                transform: openModule === index ? "rotate(45deg) scale(1.05)" : "scale(1.05)",
                                            }}
                                        >
                                            {/* Horizontal line */}
                                            <Box
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                w="14px"
                                                h="1.5px"
                                                bg="black"
                                            />
                                            {/* Vertical line */}
                                            <Box
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                w="1.5px"
                                                h="14px"
                                                bg="black"
                                            />
                                        </Box>
                                    </Flex>
                                </Flex>
                            </Box>
                        ))}
                    </HStack>
                </Box>
            </Box>

            {/* Team/Evidence Section - Whoop-style Cycling Carousel */}
            <Box
                ref={carouselSectionRef}
                minH={{ base: "auto", md: "80vh" }}
                py={{ base: 16, md: 24 }}
                display="flex"
                alignItems="center"
                position="relative"
                overflow="hidden"
                bg="#000000"
                isolation="isolate"
            >
                {/* Video Background - switches based on active feature */}
                <video
                    ref={carouselVideoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    src={featureItems[0].video}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                        pointerEvents: "none",
                        backgroundColor: "#000000",
                    }}
                />
                {/* Dark Gradient Overlay */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={1}
                    bgGradient="linear(to-r, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0.4) 100%)"
                />

                <Container maxW="1400px" position="relative" zIndex={2}>
                    <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 12, lg: 20 }} align="flex-start">
                        <VStack align="flex-start" spacing={8} flex="1" maxW={{ base: "100%", lg: "500px" }}>
                            <Heading
                                as="h2"
                                fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                                fontWeight="400"
                                lineHeight="1.1"
                                letterSpacing="-0.02em"
                                color="white"
                            >
                                Built for your
                                <br />
                                surgical success
                            </Heading>

                            <VStack align="flex-start" spacing={0} w="full">
                                {featureItems.map((item, index) => (
                                    <Box
                                        key={index}
                                        display="flex"
                                        alignItems="flex-start"
                                        w="full"
                                        py={4}
                                        cursor="pointer"
                                        onClick={() => {
                                            setActiveFeatureIndex(index);
                                            setFeatureProgress(0);
                                        }}
                                    >
                                        <Box
                                            w="3px"
                                            minH={activeFeatureIndex === index ? "80px" : "24px"}
                                            bg="whiteAlpha.200"
                                            mr={5}
                                            borderRadius="full"
                                            position="relative"
                                            overflow="hidden"
                                            transition="min-height 0.3s ease"
                                        >
                                            <Box
                                                position="absolute"
                                                top={0}
                                                left={0}
                                                w="100%"
                                                h={activeFeatureIndex === index ? `${featureProgress}%` : index < activeFeatureIndex ? "100%" : "0%"}
                                                bg="white"
                                                borderRadius="full"
                                                transition={activeFeatureIndex === index ? "none" : "height 0.3s ease"}
                                            />
                                        </Box>
                                        <VStack align="flex-start" spacing={2} flex="1">
                                            <Text
                                                fontSize={activeFeatureIndex === index ? { base: "lg", md: "xl" } : { base: "md", md: "lg" }}
                                                fontWeight={activeFeatureIndex === index ? "500" : "400"}
                                                color={activeFeatureIndex === index ? "white" : "whiteAlpha.500"}
                                                transition="all 0.3s ease"
                                                lineHeight="1.3"
                                            >
                                                {item.title}
                                            </Text>
                                            <Box
                                                maxH={activeFeatureIndex === index ? "100px" : "0px"}
                                                overflow="hidden"
                                                transition="max-height 0.4s ease, opacity 0.3s ease"
                                                opacity={activeFeatureIndex === index ? 1 : 0}
                                            >
                                                <Text
                                                    fontSize={{ base: "sm", md: "md" }}
                                                    color="whiteAlpha.700"
                                                    lineHeight="1.6"
                                                    fontWeight="400"
                                                >
                                                    {item.description}
                                                </Text>
                                            </Box>
                                        </VStack>
                                    </Box>
                                ))}
                            </VStack>

                            <Button
                                size="lg"
                                bg="white"
                                color="black"
                                _hover={{
                                    bg: "whiteAlpha.900",
                                    transform: "translateY(-2px)",
                                }}
                                as={RouterLink}
                                to="/about"
                                px={10}
                                py={7}
                                borderRadius="full"
                                fontWeight="500"
                                textTransform="uppercase"
                                letterSpacing="0.1em"
                                transition="all 0.3s"
                                mt={4}
                            >
                                Meet Our Team
                            </Button>
                        </VStack>
                    </Flex>
                </Container>
            </Box>

            {/* Research Section */}
            <Box bg="#0a0a0a" py={{ base: 16, md: 24 }}>
                <Container maxW="1400px">
                    <VStack spacing={16}>
                        <VStack spacing={4} textAlign="center">
                            <Text
                                fontSize="sm"
                                textTransform="uppercase"
                                letterSpacing="0.25em"
                                color="whiteAlpha.500"
                                fontWeight="400"
                            >
                                The Science
                            </Text>
                            <Heading
                                as="h2"
                                fontSize={{ base: "3xl", md: "5xl" }}
                                fontWeight="500"
                                letterSpacing="-0.02em"
                            >
                                Backed by research.
                            </Heading>
                            <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.800" maxW="720px" lineHeight="1.7">
                                Research indicates that structured prehabilitation, in some types of surgery and for some people, may lead to:
                            </Text>
                        </VStack>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full" maxW="900px">
                            {RESEARCH_FIGURES.map((f) => (
                                <VStack key={f.label} spacing={2} textAlign="center">
                                    <Text fontSize={{ base: "5xl", md: "6xl" }} fontWeight="500" bgGradient="linear(to-r, #4A90E2, #6BC4F0)" bgClip="text" letterSpacing="-0.02em">
                                        {f.value}
                                    </Text>
                                    <Text fontSize="md" color="whiteAlpha.800">
                                        {f.label}<sup>{f.ref}</sup>
                                    </Text>
                                </VStack>
                            ))}
                        </SimpleGrid>

                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
                            <Box
                                bg="rgba(255,255,255,0.02)"
                                border="1px solid"
                                borderColor="whiteAlpha.100"
                                borderRadius="2xl"
                                p={8}
                            >
                                <VStack align="flex-start" spacing={4}>
                                    <Box
                                        p={3}
                                        bg="rgba(74, 144, 226, 0.15)"
                                        borderRadius="xl"
                                    >
                                        <Icon as={FaDumbbell} w={6} h={6} color="#4A90E2" />
                                    </Box>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontStyle="italic" lineHeight="1.8" fontWeight="300">
                                        "Preoperative training may be beneficial for surgical populations through improvement of exercise capacity and reduced postoperative complications (by 56%)"
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontWeight="400">
                                        Clifford K et al. JAMA Netw Open. 2023
                                    </Text>
                                </VStack>
                            </Box>

                            <Box
                                bg="rgba(255,255,255,0.02)"
                                border="1px solid"
                                borderColor="whiteAlpha.100"
                                borderRadius="2xl"
                                p={8}
                            >
                                <VStack align="flex-start" spacing={4}>
                                    <Box
                                        p={3}
                                        bg="rgba(74, 144, 226, 0.15)"
                                        borderRadius="xl"
                                    >
                                        <Icon as={FaAppleAlt} w={6} h={6} color="#4A90E2" />
                                    </Box>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontStyle="italic" lineHeight="1.8" fontWeight="300">
                                        "Nutritional prehabilitation reduces risk of complications, mortality, and length of stay"
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontWeight="400">
                                        McIsaac DI et al. Br J Anaesth. 2022
                                    </Text>
                                </VStack>
                            </Box>

                            <Box
                                bg="rgba(255,255,255,0.02)"
                                border="1px solid"
                                borderColor="whiteAlpha.100"
                                borderRadius="2xl"
                                p={8}
                            >
                                <VStack align="flex-start" spacing={4}>
                                    <Box
                                        p={3}
                                        bg="rgba(74, 144, 226, 0.15)"
                                        borderRadius="xl"
                                    >
                                        <Icon as={FaBrain} w={6} h={6} color="#4A90E2" />
                                    </Box>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontStyle="italic" lineHeight="1.8" fontWeight="300">
                                        "Mindfulness meditation led to significantly less preoperative pain and anxiety, decreased pain medication, and increased physical functioning"
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.600" fontWeight="400">
                                        Hanley AW et al. Pain. 2021
                                    </Text>
                                </VStack>
                            </Box>
                        </SimpleGrid>

                        <Box w="full" maxW="900px">
                            <Text fontSize="sm" color="whiteAlpha.700" mb={3}>
                                These figures come from published research on prehabilitation in general. They are not results of the SurgReady program.
                            </Text>
                            <Text fontSize="sm" color="whiteAlpha.700" fontWeight="600" mb={2}>References</Text>
                            <VStack as="ol" align="flex-start" spacing={2} pl={5} sx={{ listStyleType: "decimal" }}>
                                {REFERENCES.map((r) => (
                                    <Text as="li" key={r.href} fontSize="sm" color="whiteAlpha.700" lineHeight="1.6">
                                        {r.text}{" "}
                                        <Link href={r.href} isExternal color="#7AB0FF" textDecoration="underline">PubMed</Link>
                                    </Text>
                                ))}
                            </VStack>
                        </Box>
                    </VStack>
                </Container>
            </Box>

            {/* Got questions? — Sam: "A section on FAQs" (replaces the separate FAQ page) */}
            <Box bg="#000000" py={{ base: 16, md: 24 }}>
                <Container maxW="900px">
                    <Heading as="h2" fontSize={{ base: "3xl", md: "5xl" }} fontWeight="500" letterSpacing="-0.02em" textAlign="center" mb={10}>
                        Got questions? We&apos;ve got answers.
                    </Heading>
                    <Accordion allowMultiple>
                        {FAQS.map((f) => (
                            <AccordionItem key={f.q} borderColor="whiteAlpha.200">
                                <AccordionButton minH="56px" py={4} _hover={{ bg: "whiteAlpha.50" }}>
                                    <Text flex="1" textAlign="left" fontSize={{ base: "md", md: "lg" }} fontWeight="500">{f.q}</Text>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={5}>
                                    <Text fontSize="md" color="whiteAlpha.800" lineHeight="1.7">{f.a}</Text>
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box
                py={{ base: 20, md: 32 }}
                textAlign="center"
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgGradient="radial(ellipse at center, rgba(74, 144, 226, 0.15) 0%, transparent 70%)"
                />

                <Container maxW="800px" position="relative" zIndex={2}>
                    <VStack spacing={8}>
                        <Heading
                            as="h2"
                            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                            fontWeight="500"
                            lineHeight="1.1"
                            letterSpacing="-0.02em"
                        >
                            Ready to get SurgReady?
                        </Heading>
                        <Text
                            fontSize={{ base: "lg", md: "xl" }}
                            color="whiteAlpha.600"
                            maxW="500px"
                            fontWeight="400"
                            lineHeight="1.7"
                        >
                            Start preparing for your procedure with SurgReady&apos;s
                            evidence-informed program.
                        </Text>
                        <HStack spacing={4} pt={4}>
                            <Button
                                size="lg"
                                bg="white"
                                color="black"
                                _hover={{
                                    bg: "whiteAlpha.900",
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 20px 40px rgba(74, 144, 226, 0.3)",
                                }}
                                as={RouterLink}
                                to="/pricing"
                                px={12}
                                py={7}
                                borderRadius="full"
                                fontWeight="500"
                                letterSpacing="0.02em"
                                transition="all 0.3s"
                            >
                                Get Started
                                <ChevronRightIcon ml={2} />
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            {/* Waitlist — capture interest while the paid program is pre-launch (feeds LeadFlow) */}
            <MotionBox
                initial="hidden"
                whileInView="visible"
                variants={waitlistReveal}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                py={{ base: 12, md: 20 }}
                bg="#050505"
            >
                <Container maxW={{ base: "100%", md: "720px" }}>
                    <Box
                        bg="#070707"
                        borderRadius={{ base: "lg", md: "2xl" }}
                        p={{ base: 6, md: 10 }}
                        boxShadow="0 10px 30px rgba(0,0,0,0.6)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.04)"
                    >
                        <VStack spacing={4} mb={6} textAlign="center">
                            <Heading as="h2" fontSize={{ base: "xl", md: "3xl" }} fontWeight="700" letterSpacing="-0.01em" color="white">
                                Join the waitlist
                            </Heading>
                            <Text fontSize={{ base: "sm", md: "md" }} color="whiteAlpha.700" maxW={{ base: "100%", md: "560px" }}>
                                SurgReady is opening soon. Leave your details and we'll let you know the moment it's available.
                            </Text>
                        </VStack>

                        {/* Form (embed) — style inputs and buttons via wrapper sx so logic remains unchanged */}
                        <Box
                            className="waitlist-embed"
                            sx={{
                                'form': { width: '100%' },
                                'input, textarea, select': {
                                    minHeight: '44px',
                                    fontSize: ['16px', null, '16px'],
                                    padding: ['10px', null, '14px'],
                                    borderRadius: '10px',
                                    border: '1px solid',
                                    borderColor: 'rgba(255,255,255,0.08)',
                                    background: 'rgba(255,255,255,0.02)',
                                    color: 'white',
                                    _placeholder: { color: 'whiteAlpha.400' },
                                },
                                'input:focus, textarea:focus, select:focus': {
                                    boxShadow: '0 6px 18px rgba(74,144,226,0.12)',
                                    borderColor: 'rgba(74,144,226,0.9)'
                                },
                                'label': { color: 'white', fontSize: '14px', fontWeight: 600 },
                                '.chakra-button[type="submit"]': {
                                    borderRadius: '12px',
                                    paddingY: ['12px', null, '14px'],
                                    fontSize: ['16px', null, '17px'],
                                    boxShadow: '0 12px 30px rgba(74,144,226,0.15)',
                                    background: 'linear-gradient(90deg, rgba(74,144,226,1) 0%, rgba(59,130,246,1) 100%)',
                                    color: 'white',
                                },
                                '.chakra-button[type="submit"]:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 18px 40px rgba(74,144,226,0.22)'
                                }
                            }}
                        >
                            <PublicForm slug="waitlist" embed redirectTo={null} />
                        </Box>
                    </Box>
                </Container>
            </MotionBox>

            <Box ref={footerRef}>
                <FooterWithFourColumns />
            </Box>

            {/* Floating CTA Button - Appears after scrolling past hero, hides when footer visible */}
            <Box
                position="fixed"
                bottom={8}
                left="0"
                right="0"
                display="flex"
                justifyContent="center"
                zIndex={999}
                opacity={showFloatingCTA && !footerVisible ? 1 : 0}
                pointerEvents={showFloatingCTA && !footerVisible ? "auto" : "none"}
                transition="opacity 0.3s ease"
            >
                <Button
                    ref={floatingButtonRef}
                    size="lg"
                    bg={floatingButtonStyle === 'light' ? "white" : "black"}
                    color={floatingButtonStyle === 'light' ? "black" : "white"}
                    px={10}
                    py={7}
                    borderRadius="full"
                    fontWeight="500"
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                    boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
                    border="1px solid"
                    borderColor={floatingButtonStyle === 'light' ? "transparent" : "whiteAlpha.300"}
                    _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5)",
                    }}
                    transition="all 0.3s ease"
                    as={RouterLink}
                    to="/pricing"
                >
                    Get SurgReady
                </Button>
            </Box>
        </Box>
    );
};

export default Homepage;
